import React from 'react';

import { FormikErrors } from 'formik';

import { MenuItem } from '@material-ui/core';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

import { IEmployee } from '../../../../../state/app/registers/employees';
import { ITank } from '../../../../../state/app/registers/tanks';
import { IVehicle } from '../../../../../state/app/registers/vehicles';

import TranslationHelper from '../../../../../helpers/TranslationHelper';

import {
    STANDARD_LOGIC,
    REVERSED_LOGIC,
} from '../../../../../constants/dictionaries/TankLogic';

import FieldWrapper from '../../../../common/formikWrappers/FieldWrapper';
import {
    employeeEditSchema,
    tankEditSchema,
    vehicleEditSchema,
} from '../../../../../schemas';

export type TFormData = IVehicle | IEmployee | ITank;

export const objectTypes = {
    TANK: 14,
    VEHICLE: 8,
    EMPLOYEE: 3,
};

const getCommonProps = (
    name: string,
    errors: FormikErrors<TFormData>,
    classes: ClassNameMap<'field' | 'paneContent'>
) => {
    const path = name.split('.');
    let error: FormikErrors<TFormData> | string = errors;
    path.forEach((el) => {
        if (error && error[el]) {
            error = error[el];
        } else {
            error = '';
        }
    });

    return {
        name,
        key: name,
        error: !!error,
        helperText: error,
        fullWidth: true,
        component: FieldWrapper,
        className: classes.field,
    };
};

export const vehicleFields = (
    errors: FormikErrors<TFormData>,
    classes: ClassNameMap<'field' | 'paneContent'>
) => [
    {
        ...getCommonProps('name', errors, classes),
        required: true,
        type: 'name',
        label: TranslationHelper.translate('Object name'),
    },
    {
        ...getCommonProps('timeZone', errors, classes),
        type: 'timeZone',
    },

    {
        ...getCommonProps('groups', errors, classes),
        required: true,
        type: 'groups',
    },
    {
        ...getCommonProps('plateNumber', errors, classes),
        label: TranslationHelper.translate('Vehicle plate number'),
    },

    {
        ...getCommonProps('sideNumber', errors, classes),
        label: TranslationHelper.translate('Vehicle side number'),
    },
    {
        ...getCommonProps('mark', errors, classes),
        label: TranslationHelper.translate('Vehicle mark'),
    },
    {
        ...getCommonProps('model', errors, classes),
        label: TranslationHelper.translate('Vehicle model'),
    },
    {
        ...getCommonProps('vin', errors, classes),
        label: TranslationHelper.translate('VIN'),
    },
    {
        ...getCommonProps('productionYear', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('Vehicle production year'),
    },
    {
        ...getCommonProps('fuelConsumption', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('Vehicle fuel consumption'),
    },
    {
        ...getCommonProps('fuelConsumptionType', errors, classes),
        label: '',
        select: true,
        children: [
            <MenuItem value={'km'} key={'fuelConsumptionKm'}>
                l/100km
            </MenuItem>,
            <MenuItem value={'mth'} key={'fuelConsumptionMth'}>
                l/h (l/mth)
            </MenuItem>,
        ],
    },
    {
        ...getCommonProps('tankSize', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('Vehicle fuel capacity'),
    },
];

export const employeeFields = (
    errors: FormikErrors<TFormData>,
    classes: ClassNameMap<'field' | 'paneContent'>
) => [
    {
        ...getCommonProps('name', errors, classes),
        required: true,
        type: 'name',
        label: TranslationHelper.translate('Object name'),
    },
    {
        ...getCommonProps('timeZone', errors, classes),
        type: 'timeZone',
    },
    {
        ...getCommonProps('groups', errors, classes),
        required: true,
        type: 'groups',
    },
    {
        ...getCommonProps('firstName', errors, classes),
        required: true,
        label: TranslationHelper.translate('First name'),
    },
    {
        ...getCommonProps('surname', errors, classes),
        required: true,
        label: TranslationHelper.translate('Surname'),
    },
    {
        ...getCommonProps('contact.email', errors, classes),
        label: TranslationHelper.translate('Email'),
    },
    {
        ...getCommonProps('contact.phoneNumber', errors, classes),
        label: TranslationHelper.translate('Phone'),
    },
    {
        ...getCommonProps('contact.mobileNumber', errors, classes),
        label: TranslationHelper.translate('Mobile'),
    },
    {
        ...getCommonProps('address.street', errors, classes),
        label: TranslationHelper.translate('Street'),
    },
    {
        ...getCommonProps('address.houseNumber', errors, classes),
        label: TranslationHelper.translate('House number'),
    },
    {
        ...getCommonProps('address.apartmentNumber', errors, classes),
        label: TranslationHelper.translate('Flat number'),
    },
    {
        ...getCommonProps('address.postalCode', errors, classes),
        label: TranslationHelper.translate('Postal code'),
    },
    {
        ...getCommonProps('address.city', errors, classes),
        label: TranslationHelper.translate('City'),
    },
];

export const tankFields = (
    errors: FormikErrors<TFormData>,
    classes: ClassNameMap<'field' | 'paneContent'>
) => [
    {
        ...getCommonProps('name', errors, classes),
        required: true,
        type: 'name',
        label: TranslationHelper.translate('Object name'),
    },
    {
        ...getCommonProps('timeZone', errors, classes),

        type: 'timeZone',
    },
    {
        ...getCommonProps('groups', errors, classes),
        required: true,
        type: 'groups',
    },
    {
        ...getCommonProps('capacity', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('Capacity'),
    },
    {
        ...getCommonProps('reserve', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('FuelTank_Reserve'),
    },
    {
        ...getCommonProps('tareWeight', errors, classes),
        type: 'number',
        label: TranslationHelper.translate('FuelTank_Weight'),
    },
    {
        ...getCommonProps('logicType', errors, classes),
        label: TranslationHelper.translate('Logic'),
        select: true,
        children: [
            <MenuItem value={STANDARD_LOGIC} key={'logicType' + STANDARD_LOGIC}>
                {TranslationHelper.translate('Simple')}
            </MenuItem>,
            <MenuItem value={REVERSED_LOGIC} key={'logicType' + REVERSED_LOGIC}>
                {TranslationHelper.translate('Reversed')}
            </MenuItem>,
        ],
    },
    {
        ...getCommonProps('fuelType', errors, classes),
        label: TranslationHelper.translate('Content'),
    },
    {
        ...getCommonProps('description', errors, classes),
        label: TranslationHelper.translate('Description'),
    },
];

export const fields = {
    [objectTypes.VEHICLE]: vehicleFields,
    [objectTypes.EMPLOYEE]: employeeFields,
    [objectTypes.TANK]: tankFields,
};

export const schemas = {
    [objectTypes.VEHICLE]: vehicleEditSchema,
    [objectTypes.EMPLOYEE]: employeeEditSchema,
    [objectTypes.TANK]: tankEditSchema,
};
